import React from 'react';
import {SectionList, SectionListRenderItemInfo} from 'react-native';
import {useSelector} from 'react-redux';

import {Button} from '../../components/Button';
import {HeatProfileCard} from '../../components/HeatProfileCard';
import {SafeAreaView} from '../../components/SafeAreaView';
import {SectionTitle} from '../../components/SectionTitle';
import {SwitchableBackground} from '../../components/SwitchableBackground';
import {Constants} from '../../constants';
import {Strings} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {useSpinner} from '../../contexts/spinner';
import {CheckIcon, ChevronLeftIcon} from '../../icons';
import {useAdaptiveSafeArea} from '../../lib/hooks/useAdaptiveSafeArea';
import {useAppHeaderHeight} from '../../lib/hooks/useAppHeaderHeight';
import {useAssignMoodLightToHeatProfile} from '../../lib/hooks/useAssignMoodLightToHeatProfile';
import {useDisconnectGuard} from '../../lib/hooks/useDisconnectGuard';
import {useTheme} from '../../lib/hooks/useTheme';
import {currentDeviceHeatProfilesSelector} from '../../lib/redux/slices/bleSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../../lib/redux/slices/moodLightSlice';
import {accountHeatProfilesSelector} from '../../lib/redux/slices/profilesSlice';
import {HeatProfile, getHeatProfileMoodLight} from '../../lib/types';
import type {ControlCenterNavigatorScreenProps} from '../../navigators/HomeDrawerNavigator';

export interface Props {
  moodLightId: string;
}

type ScreenProps = ControlCenterNavigatorScreenProps<
  typeof Screens.AssignToAHeatProfile
>;

export const AssignToAHeatProfileScreen = ({
  navigation,
  route,
}: ScreenProps) => {
  useDisconnectGuard();
  const theme = useTheme();

  const deviceHeatProfiles = useSelector(currentDeviceHeatProfilesSelector);
  const accountHeatProfiles = useSelector(accountHeatProfilesSelector);

  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const [selectedHeatProfileId, setSelectedHeatProfileId] =
    React.useState<string>('');

  const HEADER_HEIGHT = useAppHeaderHeight();

  const moodLightId = route.params.moodLightId;

  const [{isLoading}, assign] = useAssignMoodLightToHeatProfile();

  useSpinner({
    isVisible: isLoading,
    text: Strings.SAVING,
    color: theme.alternateSpinnerColor,
  });

  useAdaptiveSafeArea();

  React.useEffect(() => {
    navigation.setOptions({
      headerTitle: 'ASSIGN TO A HEAT PROFILE',
      headerTitleStyle: theme.navMenuTitleStyle,
      headerLeft: () => (
        <Button variant="transparent" onPress={() => navigation.goBack()}>
          <ChevronLeftIcon color={theme.navMenuIconColor} />
        </Button>
      ),
      headerRight: selectedHeatProfileId
        ? () => (
            <Button
              variant="transparent"
              onPress={() =>
                !isLoading &&
                assign({heatProfileId: selectedHeatProfileId, moodLightId})
              }>
              <CheckIcon size={32} color={theme.navMenuIconColor} />
            </Button>
          )
        : undefined,
      gestureEnabled: false,
    });
  }, [
    navigation,
    assign,
    selectedHeatProfileId,
    isLoading,
    moodLightId,
    theme.navMenuIconColor,
    theme.navMenuTitleStyle,
  ]);

  const renderItem = React.useCallback(
    ({
      item: profile,
    }: SectionListRenderItemInfo<
      HeatProfile,
      {key: string; data: HeatProfile[]}
    >) => {
      const moodLight = getHeatProfileMoodLight(moodLights)(profile);

      return (
        <HeatProfileCard
          moodLight={moodLight}
          borderColor={
            theme.assignToAHeatProfileScreenTheme.selectedBorderColor
          }
          borderWidth={selectedHeatProfileId === profile.id ? 3 : 0}
          theme={theme}
          profile={profile}
          onPress={() => {
            if (!isLoading) {
              setSelectedHeatProfileId(profile.id);
            }
          }}
        />
      );
    },
    [moodLights, isLoading, theme, selectedHeatProfileId],
  );

  const sections = React.useMemo(
    () => [
      {
        key: 'device',
        data: [...deviceHeatProfiles].sort((a, b) => a.order - b.order),
      },
      {
        key: `account`,
        data: [...accountHeatProfiles].sort((a, b) => a.order - b.order),
      },
    ],
    [deviceHeatProfiles, accountHeatProfiles],
  );

  if (!deviceHeatProfiles || !accountHeatProfiles) return null;

  const getSectionTitle = (key?: string) => {
    switch (key) {
      case 'device':
        return Strings.heatProfiles.sections.account;
      case 'account':
        return `${Strings.heatProfiles.sections.device} ${accountHeatProfiles.length}/${Constants.MAX_HEAT_PROFILES}`;
      default:
        return '';
    }
  };

  return (
    <SwitchableBackground
      background={theme.assignToAHeatProfileScreenTheme.background}
      style={{
        flex: 1,
      }}>
      <SafeAreaView
        style={{
          flex: 1,
          paddingTop: HEADER_HEIGHT,
          flexBasis: 0,
        }}>
        <SectionList
          {...{sections}}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          renderSectionHeader={item => (
            <SectionTitle
              style={{paddingHorizontal: 8}}
              title={getSectionTitle(item.section.key)}
            />
          )}
          stickySectionHeadersEnabled={false}
        />
      </SafeAreaView>
    </SwitchableBackground>
  );
};
